import {Inject, Injectable, InjectionToken} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {EquipmentId} from '@app/graphql-typings/graphql';
import {RulesService, TaskScopeOptions} from '@app/layout/rules/rulesmanager/rules.service';
import {Task, TaskId} from '@shared/services/api/task.interface';
import {CollectionUtils} from '@shared/util/collections/collection-utils';
import {CacheByParentId} from './cache-by-parent-id';

/** Token that determines the fields fetched by TasksCache fetch. This is the default scope which has no fields added. */
export const TASK_SCOPE_OPTIONS = new InjectionToken<TaskScopeOptions>('TaskScopeOptions', {
  providedIn: 'root',
  factory: () => ({})
});

/** Scope options for charts page. This is the value provided for TASK_SCOPE_OPTIONS in charts page. */
export const CHART_TASK_SCOPE_OPTIONS: TaskScopeOptions = {getAlertSchedule: true};

/** Configures whether `TasksCache` keeps what it fetches or is just a pass through */
export const TASKS_CACHING_ENABLED = new InjectionToken<boolean>('TasksCachingEnabled', {
  providedIn: 'root',
  factory: () => true
});

export abstract class TasksCache {
  abstract getTasksForEquipment(equipmentId: EquipmentId): Observable<Task[]>;
  abstract getTasksForEquipmentIds(equipmentIds: EquipmentId[]): Observable<Map<EquipmentId, Task[]>>;
  abstract getTasksByIds(taskIds: TaskId[], equipmentIds?: EquipmentId[]): Observable<Map<TaskId, Task>>;
}

/** Caches tasks by equipment id and by task id. */
@Injectable()
export class TasksCacheImpl implements TasksCache {
  private readonly cache: CacheByParentId<EquipmentId, TaskId, Task>;

  constructor(private readonly rulesService: RulesService,
              @Inject(TASK_SCOPE_OPTIONS) private readonly scopeOptions: TaskScopeOptions,
              @Inject(TASKS_CACHING_ENABLED) cachingEnabled = true) {
    this.cache = new CacheByParentId<EquipmentId, TaskId, Task>(
      (taskIds: TaskId[]) => this.fetchByIds(taskIds),
      (equipmentIds: EquipmentId[]) => this.fetchByEquipmentIds(equipmentIds),
      cachingEnabled
    );
  }

  getTasksForEquipment(equipmentId: EquipmentId): Observable<Task[]> {
    return this.cache.getListByParentId(equipmentId);
  }

  getTasksForEquipmentIds(equipmentIds: EquipmentId[]): Observable<Map<EquipmentId, Task[]>> {
    return this.cache.getListByParentIds(equipmentIds);
  }

  getTasksByIds(taskIds: TaskId[], equipmentIds?: EquipmentId[]): Observable<Map<TaskId, Task>> {
    return this.cache.getByIds(taskIds, equipmentIds);
  }

  private fetchByIds(taskIds: TaskId[]): Observable<Map<TaskId, Task>> {
    return this.rulesService.getTasksById(taskIds, this.scopeOptions).pipe(
      map(tasks => CollectionUtils.indexBy(tasks, 'task_id')));
  }

  private fetchByEquipmentIds(equipmentIds: EquipmentId[]): Observable<Map<EquipmentId, Map<TaskId, Task>>> {
    return this.rulesService.getTasksForEquipment(equipmentIds, this.scopeOptions).pipe(
      map(tasks => {
        const taskByIdByEquipmentId = new Map(equipmentIds.map(equipmentId =>
          <[EquipmentId, Map<TaskId, Task>]>[equipmentId, new Map<TaskId, Task>()]));
        tasks.forEach(task => task.sensors?.forEach(sensor => {
          taskByIdByEquipmentId.get(sensor.equipment_id)?.set(task.task_id, task);
        }));
        return taskByIdByEquipmentId;
      })
    );
  }
}
